// THE TREE, THE BRANCH, THE PLAN OR THE REPOSITORY'S OWN GATE — one row per failure kind in this family.
// See `../failure-kind.ts` for how the four families become `TaskFailureKind`.

import { HUMAN_PUSH_QUIET_MS } from '../../babysit/human-quiet';
import { CI_WAIT_CEILING_MS } from '../../ci-wait';
import { TaskHoldReason } from '../hold';
import {
  BabysitRoundCharge,
  charged,
  type FailureKindRow,
  platform,
  TASK_MAX_ATTEMPTS,
  TaskExhaustionAction,
  terminal,
  uncharged,
  wire,
} from './row';

/**
 * THE TREE, THE BRANCH, THE PLAN OR THE REPOSITORY'S OWN GATE — the ref, the credential, the dependency graph or the verify command stood between the work and a landed change.
 *
 * Each row is the WHOLE decision for its kind — who writes it, what it costs, whether it
 * parks, whose fault it is and what a reader is told. The doc on each key is the kind's
 * own docblock followed by the reasoning that used to sit beside its entry in each of the
 * per-concern tables (`TASK_FAILURE_PRODUCER`, `TASK_FAILURE_BUDGET`, the box-local set,
 * the re-pend hold, `BABYSIT_ROUND_CHARGE`), moved verbatim.
 */
export const TREE_FAILURE_KINDS = {
  /**
   * A `depends_on` target reached a terminal status that will never satisfy
   * ({@link TASK_DEP_UNSATISFIABLE_STATUSES}), so this task can never be claimed:
   * the claim gate admits a dependent only when EVERY dependency is satisfied,
   * and a terminal status is left only by a revive — save the settled-PR sweep's
   * `failed → merged` (#4022), which comes too late for a dependent already failed
   * here ({@link TASK_DEP_UNSATISFIABLE_STATUSES} says what then runs it). Terminal on
   * purpose and never retried — a retry mints a new task id, so the dead reference
   * stays dead.
   *
   * Not a run failure: no box ever claimed this row, there is no transcript, and
   * `attempt` is untouched. It is the DAG reporting that its own ancestor died.
   *
   * Budget:
   * TERMINAL, and the reason is structural rather than a judgement about
   * flakiness: a retry mints a NEW task id, so the dead `depends_on` reference is
   * still dead and the fresh attempt would park in exactly the same place. The
   * work is unblocked by re-authoring the plan, never by re-running this row.
   *
   * Babysit round:
   * A dependency died; the row was never claimed. Follow-ups carry no dependencies.
   *
   * Box-at-fault:
   * No box ever claimed the row: the DAG is reporting its own dead ancestor.
   * There is no machine to inspect. Both gates answer alike — `depends_on` and
   * the plan-group DAG are two edges of one graph, and neither casualty ever
   * reached a box (#2949).
   */
  DependencyFailed: {
    value: 'dependency_failed',
    ordinal: 5,
    producer: platform('packages/db/src/queries/tasks.ts#failTasksWithDeadDependencies'),
    budget: terminal(),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'A task this one depends on died and can never satisfy it, so no box could ever claim this row. Re-author the plan — a retry keeps the same dead reference.',
      unclaimed:
        'A task this depends on died before any box could claim this row, so the work never started.',
      ran: 'A task this depends on died and can never satisfy it, so no box could ever claim this row. Re-author the plan — a retry keeps the same dead reference.',
    },
  },
  /**
   * The PLAN-GROUP gate's casualty, split from {@link DependencyFailed} (#2949).
   *
   * Both mean "a dependency the claim gate will never accept", and for a while
   * they shared one string on that reasoning. The sweep that writes this one said
   * so in as many words — that a second failure kind "would split one question
   * across two columns" — and that sentence was wrong twice over: a second member
   * of THIS enum is one column, not two, and the two facts have different
   * diagnoses and different remedies. A `dependency_failed` row means a task named
   * in `depends_on` died. A `plan_dependency_failed` row means an upstream
   * `plan_groups` slice of the same plan can never reach `merged` — and its
   * `depends_on` may be empty, or point at a task that COMPLETED successfully,
   * which is exactly the reading that cost a live DB walk to diagnose.
   *
   * Budgeted identically to its sibling ({@link TaskBudgetKind.Terminal}) because
   * the retry answer is the same: nothing about re-running THIS row advances a
   * plan whose upstream slice is finished. Only the operator's next move differs,
   * and that is what the split makes readable off `tasks.failure_kind` instead of
   * off `audit_events`.
   *
   * Producer:
   * THE ENTRY THIS TABLE WAS MISSING, and the split is what makes it namable
   * (#2949). `failTasksWithDeadPlanDependency` has always written a terminal
   * failure kind and this table never named it: it wrote `dependency_failed`,
   * whose only declared producer is the `depends_on` sweep (`DependencyFailed`'s). The reverse scan
   * could not catch it either — `assignsCode` matches a `code:` stamp, and a
   * PLATFORM writer sets `failureKind:` straight onto the row — which is the
   * known hole that entry's own docblock records for `worktree-store.ts`.
   *
   * Budget:
   * The plan-group sibling (#2949): the same verdict for the same reason — the
   * upstream slice is finished and re-running THIS row cannot advance it — so the
   * budget is identical and the split is about what an operator READS, not about
   * what the queue does next.
   *
   * Babysit round:
   * An upstream plan slice can never merge; never claimed. Not a babysit producer.
   *
   * Box-at-fault:
   * No box ever claimed the row: the DAG is reporting its own dead ancestor.
   * There is no machine to inspect. Both gates answer alike — `depends_on` and
   * the plan-group DAG are two edges of one graph, and neither casualty ever
   * reached a box (#2949).
   *
   * Alert sentence:
   * Its own sentence, and the whole reason the kind was split (#2949): the
   * sibling `DependencyFailed` sends a reader to `depends_on`, which on one of these rows is
   * routinely EMPTY — or names a task that completed successfully.
   *
   * Reason sentence:
   * Split from `DependencyFailed` (#2949) because it sends the reader somewhere
   * else: `depends_on` on one of these rows is routinely empty, or names a task
   * that completed. The plan is where the answer is.
   */
  PlanDependencyFailed: {
    value: 'plan_dependency_failed',
    ordinal: 6,
    producer: platform('packages/db/src/queries/tasks.ts#failTasksWithDeadPlanDependency'),
    budget: terminal(),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'An earlier slice of this task’s plan can never reach `merged`, so the plan-group gate would hold this row forever. Look at the plan, not at this task — `depends_on` is not where the answer is.',
      unclaimed:
        'An earlier slice of this task’s plan can never reach `merged`, so no box could claim this row and the work never started.',
      ran: 'An earlier slice of this task’s plan can never reach `merged`, so the plan-group gate would hold this row forever. Look at the plan — re-authoring or re-dispatching that slice is what unblocks this.',
    },
  },
  /**
   * The base branch moved while the run was running, and the patch no longer
   * applies cleanly. Distinct from {@link RunnerError}: the patch was correct
   * once, and the failure is a property of the repo state, not the box.
   *
   * Budgeted to ONE attempt (#2572): the issue's deterministic class — see
   * {@link TASK_FAILURE_BUDGET} for the evidence and the revert-the-map
   * rollback if the post-deploy terminal-rate read shows a retry-recovering
   * population. Produced by the wire `code` map ({@link TASK_FAILURE_WIRE_CODE}).
   *
   * Babysit round:
   * Someone else moved the tree mid-run (the `remote_moved` refusal) — a race, not the fix.
   *
   * Box-at-fault:
   * Repo state and dispatch-time facts, through `NoPrOpened`. The same box runs
   * the next task fine, and the next box fails this one the same way.
   */
  TreeDiverged: {
    value: 'tree_diverged',
    ordinal: 8,
    producer: wire('apps/runner/src/modes/worker/task-tree.ts#TaskTreeError'),
    budget: charged(1, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The base branch moved while the run was working, so its patch no longer applies cleanly.',
      unclaimed:
        'No box claimed this — the base branch was already diverged when the row was offered.',
      ran: 'The base branch moved while the run was working, so its patch no longer applies cleanly.',
    },
  },
  /**
   * The base branch moved between the harness checkout's open and its ship, and
   * the ship-time rebase onto the fresh base hit a conflict the harness refuses
   * to resolve. A fact about the REPOSITORY'S STATE at one instant — not the box
   * (the same box re-opens fine on the new base) and not the work (nothing was
   * graded; the diff never reached the gate). Distinct from {@link TreeDiverged},
   * whose ONE-attempt ladder is for a patch population that no longer applies:
   * this one clears by itself on the next open, so a charged strike would end a
   * task on a race it never caused.
   *
   * Budgeted `Uncharged`, capped at three consecutive (#4478): the attempt counter
   * does not move and the row re-enters the queue; the cap is what ends it under
   * continuous churn rather than letting it re-pend forever. NOT box-local — see
   * {@link taskFailureBoxLocal} — because excluding the box that saw the remote
   * move would exclude a box that did nothing wrong. Produced by the wire `code`
   * map ({@link TASK_FAILURE_WIRE_CODE}) at the ship-time sites named in
   * {@link TASK_FAILURE_PRODUCER}.
   *
   * Producer:
   * Three ship-time sites. The rebase onto the freshly fetched base inside `push`,
   * refused on conflict (#4478) — the open-time adopt arms are #4479's and are
   * deliberately not listed here. And a `resolve_conflict` round's push
   * (`finishConflictMerge`, `apps/runner/src/modes/worker/conflict-merge.ts`): it
   * re-fetches the base before pushing, and a base that moved and conflicts again is
   * refused `base_moved` by `mergeFreshBase` (`conflict-base-fresh.ts`) instead of
   * pushing a merge already stale (#6849). And the first push's base re-read
   * (`baseDriftRefusal`, `apps/runner/src/modes/worker/team/git/base-drift.ts`, #7411):
   * before the branch's first pull request exists, the push fetches the base once
   * more, and a base that moved and changed a path this run's commit also changes is
   * refused `base_moved` rather than opening a duplicate that conflicts add/add with
   * what landed (tesote/bank-integrations, a person's fix for the same note merged
   * mid-run). Uncharged on the task, but the babysit
   * conflict streak COUNTS each run of such a round (`uncharged_streak`, so a row that
   * re-pended three times is three; `conflictResolveStreakFor`, `apps/worker`), so a base
   * that never stops moving escalates `base_kept_moving` rather than minting resolve
   * rounds for ever.
   *
   * And the FIRST PUSH's base re-read (`base-drift.ts`, `baseDriftRefusal`): a branch
   * with no lease opens the pull request, so that push re-fetches the base and refuses
   * `base_moved` when the base moved AND changed a path this run's commit changes —
   * the same kind and the same seam, refused before a pull request rides a base the
   * work was written against.
   *
   * Budget:
   * The ship-time rebase refused because the base moved under the run (#4478):
   * nothing was graded, so nothing is charged, and the next open lands on the fresh
   * base by itself. Capped like the other refusals so continuous churn ends the
   * task as `refusals_exhausted` rather than re-pending it forever.
   *
   * Babysit round:
   * The base moved between open and ship; clears on the next open (#4478). A
   * `resolve_conflict` round is the exception: a base that moved under its resolution may
   * keep moving, so the conflict streak (`conflictResolveStreakFor`, `apps/worker`) COUNTS
   * each `base_moved` run of a resolve row although this table excuses it (#6849).
   *
   * Box-at-fault:
   * Repo state and dispatch-time facts, through `NoPrOpened`. The same box runs
   * the next task fine, and the next box fails this one the same way.
   *
   * Reason sentence:
   * #4478 — the ship-time rebase refused because the base moved under the run: a
   * fact about the repository at one instant, not the box and not the work, so no
   * attempt was charged and the row re-enters the queue for a fresh open.
   */
  BaseMoved: {
    value: 'base_moved',
    ordinal: 9,
    producer: wire(
      'apps/runner/src/modes/worker/team/git/open.ts#openHarnessCheckout',
      'apps/runner/src/modes/worker/conflict-base-fresh.ts#mergeFreshBase',
      'apps/runner/src/modes/worker/team/git/base-drift.ts#baseDriftRefusal',
    ),
    budget: uncharged(3),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The base branch moved while the session ran and the ship-time rebase hit a conflict; nothing to fix — the work re-enters the queue uncharged and reopens on the new base.',
      unclaimed:
        'No box claimed this — the base branch moved while the row was offered; nothing to fix, the work re-enters the queue and reopens on the new base.',
      ran: 'Not charged: the base branch moved while the session ran; nothing to fix — the work re-enters the queue and reopens on the new base.',
    },
  },
  /**
   * The remote head gained a commit between {@link pushOrRefuse}'s fetch and its push
   * — the `remote_moved` refusal of `FOLLOWUP_LAND_WIRE_CODE` (`followup-land.ts`),
   * a race the harness refuses to fast-forward past. Distinct from
   * {@link TreeDiverged}, whose ONE-attempt ladder is for a patch population that no
   * longer applies: this one clears by itself on the next retry, so a charged strike
   * would end a task on a race it never caused. Also distinct from {@link BaseMoved},
   * which is the SAME-ORIGIN cause at open time (the harness re-opens on the new base);
   * here the harness was already open, the follow-up re-fetches the new head, and the
   * commit applies cleanly against it.
   *
   * Budgeted `Uncharged`, capped at three consecutive (#5085): the attempt counter does
   * not move and the row re-enters the queue; the cap is what ends it under continuous
   * churn rather than letting it re-pend forever. NOT box-local — see
   * {@link taskFailureBoxLocal} — because excluding the box that saw the remote move
   * would exclude a box that did nothing wrong. Produced by the wire `code` map at
   * `followup-land.ts`'s ONE ship-time site.
   *
   * MEASURED on tesote/tesote.ai#2601, 2026-09-25: 47 of 48 measured `remote_moved`
   * tasks filed as `runner_error` (because `FOLLOWUP_LAND_WIRE_CODE.remote_moved` was
   * `null`); 16 of 48 went on to `completed`, so naming the kind ended those 16 on their
   * first fault. After #5084, the main source of these moves (base refresh merging main
   * under a pending round) is gone; the residual is a 3-attempt cap's worth of churn,
   * not the kind.
   *
   * Producer:
   * `apps/runner/src/modes/worker/followup-land-error.ts#FOLLOWUP_LAND_WIRE_CODE`'s
   * `remote_moved` arm — the only site that has ever fired this. Recorded in
   * {@link FOLLOWUP_LAND_WIRE_CODE}.
   *
   * Budget:
   * Same shape as {@link BaseMoved}: the work was not graded, so nothing is charged,
   * and the next push re-fetches the head that moved. Capped like the other uncharged
   * refusals so continuous churn ends the task as `refusals_exhausted` rather than
   * re-pending it forever.
   *
   * Babysit round:
   * The remote gained a commit between the harness's fetch and its push; the harness
   * re-fetches the new head on the next round (#5085). Cleared attempts on the round
   * ladder, not the attempt ladder.
   *
   * Box-at-fault:
   * Repo state and dispatch-time facts, through `NoPrOpened`. The same box runs the
   * next task fine, and the next box fails this one the same way.
   *
   * Reason sentence:
   * #5085 — the remote head moved between the follow-up's fetch and push: a fact about
   * the repository at one instant, not the box and not the work, so no attempt was charged
   * and the row re-enters the queue for a fresh push.
   */
  HeadMoved: {
    value: 'head_moved',
    ordinal: 55,
    producer: wire('apps/runner/src/modes/worker/followup-land-error.ts#FOLLOWUP_LAND_WIRE_CODE'),
    budget: uncharged(3),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The remote head moved between this round’s fetch and push; nothing to fix — the next round re-fetches the new head and lands on it.',
      unclaimed:
        'No box claimed this — the remote head moved between the follow-up’s fetch and push; nothing to fix, the work re-enters the queue and lands on the new head.',
      ran: 'Not charged: the remote head moved between the follow-up’s fetch and push; nothing to fix — the work re-enters the queue and lands on the new head.',
    },
  },
  /**
   * THE FIX ROUND UNDID THE PR ITSELF (#6097). A babysit round's commit returned the PR to
   * its base, in exactly one of two ways: it emptied the PR's diff (any author), or, on a
   * dependency-bot PR, it set EVERY version line the bot bumped back (pinning back a subset
   * is allowed). The harness's
   * mechanical guard (`apps/runner/src/modes/worker/pr-intent.ts`) refuses it and undoes the
   * commit before any push — the `pr_intent_reverted` refusal of `FOLLOWUP_LAND_WIRE_CODE`.
   *
   * MEASURED (tesote/tesote.ai, 2026-10-08): #2918 `ioredis 5.10.1 -> 6.0.0`, fix round
   * `tsk_da044b52` set every `ioredis` back to 5.10.1 and reverted `bun.lock`, leaving the PR
   * with 0 changed files that CI read green; #2916 `@ai-sdk/provider 3.0.10 -> 4.0.18`, the
   * same. Both MAJOR bumps: the model made CI green by undoing the PR's intent.
   *
   * Producer:
   * `apps/runner/src/modes/worker/followup-land-error.ts#FOLLOWUP_LAND_WIRE_CODE`'s
   * `pr_intent_reverted` arm — the guard's ONE refusal site in `followup-land.ts`.
   *
   * Budget:
   * TERMINAL, never retried: a retry re-derives the same brief over the same tree and the
   * same model reaches for the same revert. The re-drive is babysit's, not this row's: its
   * next pass escalates `pr_intent_reverted` (`decideBabysit`), and once the head is idle 12h
   * the stuck-PR rescue mints ONE fresh `fix_pr` on the stronger model whose brief leads with
   * "the previous round undid this PR's change" (#6346, `entities/babysit/stuck-rescue.ts`;
   * the harness still refuses a revert). If that re-parks, the machine re-arm opens a fresh
   * window, at most `MACHINE_REARMS_PER_PR` per PR (#6936,
   * `entities/babysit/machine-rearm.ts`), and once those are spent the 72h standing close
   * closes the PR and re-files its goal (`entities/babysit/standing-close.ts`).
   *
   * Babysit round:
   * The round's own work was refused — charged, like `verify_failed`.
   *
   * Box-at-fault:
   * The model's edit, not the box: the next box would produce and refuse the same commit.
   */
  PrIntentReverted: {
    value: 'pr_intent_reverted',
    ordinal: 56,
    producer: wire('apps/runner/src/modes/worker/followup-land-error.ts#FOLLOWUP_LAND_WIRE_CODE'),
    budget: terminal(),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The fix round tried to undo the pull request’s own change — it would have emptied the PR, or, on a dependency update, set every version it bumped back. The harness refused the commit before any push; the PR needs a human to adapt the code to the change or close it.',
      unclaimed:
        'An earlier fix round tried to undo the pull request’s own change and was refused before any push; it is not retried, and the PR needs a human.',
      ran: 'The fix round tried to undo the pull request’s own change — it would have emptied the PR, or, on a dependency update, set every version it bumped back. The harness refused the commit before any push, and it is not retried: adapt the code to the change by hand, or close the PR.',
    },
  },
  /**
   * THE REPOSITORY'S OWN PRE-PUSH GUARD REFUSED THE PUSH (developerz-ai/developerz.ai#7447).
   * A fix round created a file it depends on — the regression test its fix needs — OUTSIDE
   * the round's declared file set. The land's #5452 rule withholds a creation outside the
   * set (the set is EXCLUSIVE, and widening it is the ROUND's move, `widen_file_set`),
   * so the commit went out WITHOUT the file, and the repository's own pre-push guard
   * (lefthook.yml → scripts/typecheck-pushed.ts) refused the push: the withheld file was
   * still uncommitted in the checkout that guard compiles in place, and `refuse-dirty` is
   * that guard's answer.
   *
   * THE VERDICT IS THE GUARD'S, NOT THE TRANSPORT'S. The refusal is PROVEN by replaying
   * the repository's own hook (`git hook run`, the exit deciding, never the output text):
   * `pre_push_refused`, `apps/runner/src/modes/worker/followup-land-error.ts#FOLLOWUP_LAND_WIRE_CODE`.
   * A guard that PASSES, a repo with no guard at all, and a git that cannot run the probe
   * all keep `push_failed` — the probe answers one question, and an unanswered question is
   * never read as a refusal.
   *
   * AND ONLY FOR A PUSH THAT REACHED THE HOOK (developerz-ai/developerz.ai#7464's
   * review). The hook runs after the remote is contacted, so a push that DIED at the
   * transport never ran it, and its replay says nothing about that push: the push's own
   * exit class gates the replay (git refuses with 1 and dies with 128), so a transport
   * fault keeps `push_failed` — whose tree is kept for the retry — instead of being
   * renamed the guard's refusal, which drops the round's finished edits.
   *
   * Producer:
   * `apps/runner/src/modes/worker/followup-land-error.ts#FOLLOWUP_LAND_WIRE_CODE`'s
   * `pre_push_refused` arm — the land's ONE refusal site in `followup-land.ts`.
   *
   * Budget:
   * ONE attempt, and the task ends on it. The remedy is not a retry of the same task: a
   * retry re-derives the same brief over the same tree. The land itself already asked the
   * widen once (#7714, `apps/runner/src/modes/worker/followup-land-proof.ts`: a
   * `files_widen` grant and the guard passing WITH the creation land it), so a task that
   * still ends here was refused that proof. The babysit park re-runs a fresh ROUND through
   * the machine re-arm (`entities/babysit/machine-rearm.ts`, bounded per PR). The tree is
   * REMOVED, not kept (`babysit/release.ts` `KEPT_REFUSALS`).
   *
   * Babysit round:
   * UNCHARGED — OUR land withheld the file the round created (#5452), so the round's
   * work never reached the pull request and it spends no CI or findings ladder rung (the
   * head-unmoved thresholds): a refused land is not a head-unmoved fix attempt, and
   * charging it would burn a rung on work that was never graded. The TASK's own budget
   * above is unchanged — one attempt, and the task ends on it.
   *
   * Box-at-fault:
   * The round's edit, not the box: the next box would create the same file over the same
   * brief, and the same guard would refuse the same push.
   */
  PrePushRefused: {
    value: 'pre_push_refused',
    ordinal: 65,
    producer: wire('apps/runner/src/modes/worker/followup-land-error.ts#FOLLOWUP_LAND_WIRE_CODE'),
    budget: charged(1, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The repository’s own pre-push guard refused the push: the round created a file outside its own file set, so the commit did not carry the file the work depends on. The task is not retried: the platform re-runs the round up to its re-arm bound, and a re-run may add the file when its widening is granted and the guard passes with the file; once the bound is spent, the pull request stays parked for this reason.',
      unclaimed:
        'An earlier round’s push was refused by the repository’s own pre-push guard because it created a file outside its own file set. The task is not retried: the platform re-runs the round up to its re-arm bound, and a re-run may add the file when its widening is granted and the guard passes with the file; once the bound is spent, the pull request stays parked for this reason.',
      ran: 'The repository’s own pre-push guard refused the push: the round created a file outside its own file set, so the commit did not carry the file the work depends on. The task is not retried: the platform re-runs the round up to its re-arm bound, and a re-run may add the file when its widening is granted and the guard passes with the file; once the bound is spent, the pull request stays parked for this reason.',
    },
  },
  /**
   * A BABYSIT ROUND EDITED THE REPOSITORY'S OWN GATE, AND THE LAND REFUSED IT (#2923). The
   * follow-up lane fixes the code a check covers, never the check: a round that touched
   * `bin/check` (one the base already had, #7586 D3b) is refused before anything is staged
   * (`apps/runner/src/modes/worker/followup-land-gate.ts` `assertGateUntouched`). The refusal
   * is the harness working; until 2026-11-01 it carried no code and graded `runner_error` —
   * a BOX fault, so an operator went looking at a healthy machine and the mothership read
   * the box as at fault.
   *
   * WHY NOT AN EXISTING MEMBER: {@link VerifyFailed} says the gate RAN and refused the diff
   * (here it never ran — it was edited); {@link PrIntentReverted} is terminal for a revert a
   * retry re-derives; {@link ConflictResolutionRefused} names the conflict lane's checks in
   * its copy; {@link RunnerError} blames the box.
   *
   * Producer:
   * `apps/runner/src/modes/worker/followup-land-error.ts#FOLLOWUP_LAND_WIRE_CODE`'s
   * `gate_touched` arm.
   *
   * Budget:
   * CHARGED, the FULL ladder, `fail` on exhaustion — decided by what a retry buys. A retry is a
   * fresh model round over the same brief, and nothing makes it re-edit the gate: prod, 7 days
   * to 2026-11-01, 28 tasks met this refusal and 14 of them completed on a later attempt. A
   * terminal budget would have ended those 14; an uncharged one would let a model that keeps
   * editing the gate re-run with nothing bounding it. The charged ladder is that bound.
   *
   * Babysit round:
   * CHARGED — the round's own edit crossed the line, like a red gate's: the rung bounds a
   * model that keeps proposing a gate edit.
   *
   * Box-at-fault:
   * The round's edit, not the box: any box lands the same refusal over the same edit.
   */
  GateEditRefused: {
    value: 'gate_edit_refused',
    ordinal: 73,
    producer: wire('apps/runner/src/modes/worker/followup-land-error.ts#FOLLOWUP_LAND_WIRE_CODE'),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        "The fix round edited the repository's own gate (bin/check), which a follow-up round may not do inside a babysit land, so the harness refused the change and nothing was pushed. The task retries with a fresh round that leaves the gate alone; a gate change goes through its own task and pull request.",
      unclaimed:
        "An earlier fix round edited the repository's own gate (bin/check) and was refused before any push; the task is queued for a fresh round.",
      ran: "The fix round edited the repository's own gate (bin/check), which a follow-up round may not do inside a babysit land, so the harness refused the change and nothing was pushed. The box is not at fault; the task retries with a fresh round that leaves the gate alone, and a gate change goes through its own task and pull request.",
    },
  },
  /**
   * The box checked out the wrong branch — basename collision, a detached HEAD
   * into an unintended ref, a ref that resolved differently than the envelope
   * named. Distinct from {@link RunnerError} because the cause is dispatch-time,
   * not run-time.
   *
   * Budgeted to ONE attempt (#2572): one of the six kinds the issue classifies
   * as deterministic — a same-box retry cannot succeed — with revert-the-map as
   * the rollback if the post-deploy terminal-rate read shows a retry-recovering
   * population. Produced by the wire `code` map ({@link TASK_FAILURE_WIRE_CODE}).
   *
   * Producer:
   * ANCHORED ON THE MAP, not on the throw, since #4181 replaced the constructor's
   * `if (reason === 'branch_mismatch')` with a TOTAL `Record<FollowupLandRefusal,
   * TaskFailureKind | null>`. The `if` stamped one of fourteen refusal arms and left
   * the other thirteen silently in the residual, so a new arm shipped uncoded without
   * anyone deciding it should be; the map makes the compiler refuse a new arm until
   * someone writes a kind or writes `null`. Which arms are `null`, and the prod reads
   * behind the two that look codeable and are not, live on the map's own docblock.
   *
   * Babysit round:
   * The box checked out the wrong ref — a dispatch-time fault, before the work.
   *
   * Box-at-fault:
   * Repo state and dispatch-time facts, through `NoPrOpened`. The same box runs
   * the next task fine, and the next box fails this one the same way.
   */
  WrongBranch: {
    value: 'wrong_branch',
    ordinal: 10,
    producer: wire('apps/runner/src/modes/worker/followup-land-error.ts#FOLLOWUP_LAND_WIRE_CODE'),
    budget: charged(1, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The box ended up on a branch the assignment did not name, so the work did not land where it was meant to.',
      unclaimed:
        'No box claimed this — it would have ended up on a branch the assignment did not name.',
      ran: 'The box ended up on a branch the assignment did not name, so the work did not land where it was meant to.',
    },
  },
  /**
   * The run finished but never opened a pull request — push was rejected, the
   * branch is protected, the target branch's policy refuses it, or the box ran
   * out of retries on a transient push error. The box did the work and lost it
   * at the boundary: distinct from {@link RunnerError} because no throw fires
   * and the cause lives in a different layer.
   *
   * Budgeted to ONE attempt (#2572): one of the six kinds the issue classifies
   * as deterministic — a same-box retry cannot succeed — with revert-the-map as
   * the rollback if the post-deploy terminal-rate read shows a retry-recovering
   * population. Produced by the wire `code` map ({@link TASK_FAILURE_WIRE_CODE}).
   *
   * Producer:
   * The team lane's refusal to commit over a `PR.md` that may still be staged (#2818) is
   * the same fact the adapter lane reaches through `PrNotOpenedError` (`harness/ship.ts`
   * refuses, then the `pr` outcome's post-condition finds no pull request).
   *
   * Babysit round:
   * The push/PR was lost at the boundary — transport or a refusal of what the work pushed. Mixed.
   *
   * Box-at-fault:
   * Repo state and dispatch-time facts, through `NoPrOpened`. The same box runs
   * the next task fine, and the next box fails this one the same way.
   */
  NoPrOpened: {
    value: 'no_pr_opened',
    ordinal: 11,
    producer: wire(
      'apps/runner/src/modes/worker/outcome.ts#PrNotOpenedError',
      'apps/runner/src/modes/worker/team/session/errors.ts#TeamPrCopyStuckError',
    ),
    budget: charged(1, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The run did the work and never opened a pull request — the push was rejected, or the target branch refuses it.',
      unclaimed:
        'No box claimed this — the push that would have opened the PR was refused before any run started.',
      ran: 'The run did the work and never opened a pull request — the push was rejected, or the target branch refuses it.',
    },
  },
  /**
   * The git credential was invalid, expired, or revoked mid-run — auth passed
   * at clone-time and failed at push-time, or a fresh box re-cloned and the
   * installation token had rotated. Distinct from {@link RunnerError} because
   * the cause is an ACCOUNT condition (the credential), not a box fault.
   *
   * Budgeted to ONE attempt (#2572): one of the six kinds the issue classifies
   * as deterministic — a same-box retry cannot succeed — with revert-the-map as
   * the rollback if the post-deploy terminal-rate read shows a retry-recovering
   * population. It stays `Charged`, NOT {@link TaskBudgetKind.Uncharged}: the
   * failed run already billed the customer's BYOK key, and the `Uncharged`
   * re-queue needs the `no_usable_keys` pacer, which keys off the model
   * credential, not the git one. Produced by the wire `code` map
   * ({@link TASK_FAILURE_WIRE_CODE}).
   *
   * Producer:
   * EVERY SEAM THAT CAN BE REFUSED THE ONE CREDENTIAL, and the list is stated
   * as that property rather than as a count — it said "TWO producers" until
   * 2026-08-29 and there were four. One kind because the remedy is the one
   * credential; every site named because a grep for one would otherwise report
   * the others missing.
   *
   * THE GH PORT JOINS as a sixth producer: `gh` authenticates the same per-claim
   * credential git's seams carry (`GH_TOKEN`), so a 401 at `gh api` is the same
   * fact about the same credential as a 401 at `git push` — said in gh's OWN
   * vocabulary (`gh: Bad credentials (HTTP 401)`), which none of the git
   * markers match. Measured: 16 review-lane events in the 10 days to
   * 2026-09-04, every one `bunPostReview`, every one previously graded
   * `runner_error` — the full three-attempt ladder on a terminal credential
   * fault. `gh.ts` stamps DIRECTLY on `GhCliError`, through `isGhAuthFailure`
   * (`apps/runner/src/modes/worker/team/git/errors.ts`), which checks the
   * transport set FIRST exactly as the git half does (#3837), so a connect
   * blip through gh keeps `runner_error`'s ladder.
   * `worktree-bare-store.ts` stamps INDIRECTLY, through the `GIT_AUTH_CODE` its
   * OWN module declares and stamps on both of `refreshBareStore`'s refusals,
   * which is why this row anchors the constant rather than a class. The reverse
   * scan now SEES it — `assignsCode` resolves a `const` declared in the scope
   * it is handed, and the sweep asserts this file in `scanned` — so the row is
   * cross-checked rather than carried on trust. That hop is ONE and blunt: a
   * constant declared in ANOTHER module stays invisible (`team/guard.ts`'s
   * `PHANTOM_EDIT_WIRE_CODE`, stamped from `team/guard-repair.ts`), which is the
   * hole a hand-maintained list still has to cover. That file's header names
   * `GIT_AUTH_CODE` and points at this row; prose is not a guard, and the row
   * stood on nothing else for years.
   *
   * Babysit round:
   * Old table group: charged: the work, the model, or a cause the kind cannot name.
   * The git credential failed. CHARGED by coordinator ruling: a revoked installation
   * token needs a human, and the ladder's three-round hand-off is that path (#1876).
   *
   * Box-at-fault:
   * Account and credential conditions — `GitAuth` and the provider family (`./provider.ts`) alike. The
   * machine is fine and nothing on it can be fixed: the remedy is a key, a
   * quota, or the provider recovering. `provider_non_answer` and
   * `provider_payment_required` have their own fleet-alert kinds already, so
   * blaming the box would double-page a condition that is not the box's, under
   * the wrong title. The provider staying unavailable past the box's own
   * retries is an outage, not the machine (plan 101 L3 sibling) — any box would
   * have hit the same wall.
   */
  GitAuth: {
    value: 'git_auth',
    ordinal: 12,
    producer: wire(
      'apps/runner/src/checkout.ts#GitTokenRefusedError',
      'apps/runner/src/modes/worker/team/git/open.ts#GitSequenceError',
      'apps/runner/src/modes/worker/team/git/fetch-branch.ts#GitSequenceError',
      'apps/runner/src/modes/worker/team/git/resolve-branch.ts#GitSequenceError',
      'apps/runner/src/modes/worker/pr-branch.ts#PrBranchError',
      'apps/runner/src/worktree-bare-store.ts#GIT_AUTH_CODE',
      'apps/runner/src/modes/worker/task-tree.ts#TaskTreeError',
      'apps/runner/src/modes/setup/unpublished-onboarding.ts#TaskTreeError',
      'apps/runner/src/gh.ts#GhCliError',
      'apps/runner/src/modes/worker/outcome.ts#PrLookupUnauthorizedError',
    ),
    budget: charged(1, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The git credential was rejected mid-run — expired, revoked, or rotated since the checkout. Re-issue it if this repeats.',
      unclaimed:
        'No box claimed this — the git credential was rejected before any box could clone.',
      ran: 'The git credential was rejected mid-run — expired, revoked, or rotated since the checkout. Re-issue it if this repeats.',
    },
  },
  /**
   * The repo's OWN gate refused the work: the verify command the team lane runs
   * before committing (`./bin/check`, `bun run typecheck && bun run lint && bun
   * test`, whatever the repo names) exited NON-ZERO on the change the run
   * produced, so nothing was committed. Seventeen such rows in the two days to
   * 2026-08-29, every one of them inside the {@link RunnerError} bucket.
   *
   * IT IS NOT A RUNNER FAULT AND MUST NOT READ AS ONE. Nothing on the box broke:
   * the checkout, the model, the harness and git all worked, and the gate did
   * exactly what it exists to do. An operator reading `runner_error` here goes
   * looking at the box; the answer is in the diff.
   *
   * IT IS THE `red` ARM OF A FOUR-ARM PARTITION, not "everything that is not
   * 127". `session.ts` `verifyVerdict` grades one {@link VerifyVerdict} that
   * both the audit event and the throw read, and three of its arms are NOT this
   * kind: {@link GateUnrunnable} for a gate that could not execute (126/127) or
   * refused to grade (75), {@link RunKilled} for a signalled one, and
   * {@link RunTimeout} for a reaped one. This kind is what is left — the gate
   * ran to its own exit and said no — and only then is the answer in the diff.
   * (`VerifyVerdict` lives in the runner; packages never import apps, so it is
   * named here rather than linked.)
   *
   * ALSO THE REPOSITORY'S OWN PRE-PUSH GUARD, WHICH RAN AND SAID NO. The classification
   * in `apps/runner/src/modes/worker/push-hook-refusal.ts` (a decided hook exit, through
   * `classifyTailCause`) grades a pre-push hook that exited non-zero as this kind: the
   * hook ran to its own
   * exit and refused, which is the same verdict as a verify command that ran and said
   * no. The hook that could NOT RUN is {@link GateUnrunnable}'s (`./box.ts`), and the
   * guard that refused a push whose creation the land withheld is
   * {@link PrePushRefused}'s.
   *
   * ALSO THE PLATFORM'S MERGE GATE, RUN ON THE BOX BEFORE THE COMMIT (2026-11-01). A team-lane
   * run whose workflow edit the merge egress would refuse `gate_path_widens` (a new job,
   * trigger, secret, action owner, `run:` step or wider permission on our App's PR) is sent
   * back to narrow it; one no correction round narrows ends here
   * (`team/session/gate-path-widening.ts`). The same verdict: a gate ran and refused the diff.
   *
   * Budgeted identically to {@link RunnerError} — see {@link TASK_FAILURE_BUDGET}
   * on why a re-partition changes the label and not the spend.
   *
   * Budget:
   * From the old table's group header "the 2026-08-29 re-partition":
   * Both kinds it split out — `VerifyFailed` and `RunTimeout` — take rows that were `runner_error` yesterday, so both are
   * budgeted IDENTICALLY to it: `Charged`, the standard ladder, `Fail` on
   * exhaustion. A re-partition that also re-budgets moves retry behaviour under
   * cover of a naming change, and afterwards nobody can say which half of the
   * diff moved it. The split changes the LABEL; a tightening is its own change,
   * with its own evidence.
   *
   * `verify_failed` is the one that will eventually argue for a different
   * number — the gate refusing red work is not a box fault and re-running the
   * same broken change three times is three paid BYOK runs — but the argument
   * needs the counted population this kind exists to produce, and today that
   * population is zero because the kind is one commit old.
   *
   * THE NO-VERDICT KINDS WERE CONSIDERED FOR A TIGHTER LADDER HERE AND KEPT AT
   * THREE, on evidence that refutes the obvious argument. "A gate that could not
   * run will not run on the retry either" assumes the retry lands on the same
   * box, and it does not: `tsk_bc2f60d0696f4a6f85fe4f28de670ad4`'s attempts 1-2
   * adopted one box's checkout (`dirty_carried: 2`) and attempt 3 opened a fresh
   * one elsewhere. The fleet is heterogeneous — 4 prod rows are `bun: command
   * not found` while 7 of 10 online boxes run tasks fine — so for
   * `not_runnable` a redelivery genuinely CAN clear the fault, which is exactly
   * what a retry ladder is for. The `provider_quota_exhausted` precedent (its one-attempt budget of the time)
   * does not transfer: it rests on a NAMED mechanism of doom (a 7-day window
   * that cannot clear between two immediate re-pends) plus a zero all-time
   * population making the change free, and `tool_unavailable` has neither.
   * Tightening it would also re-budget the structured-output seam, which shares
   * the kind and is not implicated. So this PR changes the LABEL, as the
   * re-partition note in this Budget section requires; the tightening is its own change, and the
   * argument it needs is a `GROUP BY verdict, reason` over the `team_verify`
   * event this PR starts emitting.
   *
   * Babysit round:
   * The repo's own gate refused the change the round produced.
   *
   * Box-at-fault:
   * A gate — the repository's own verify command, or the platform's merge gate
   * on a workflow edit — ran and said no. That is the gate WORKING; the answer
   * is in the diff, and an operator sent to the box finds a healthy machine.
   */
  VerifyFailed: {
    value: 'verify_failed',
    ordinal: 20,
    producer: wire(
      'apps/runner/src/modes/worker/team/session/errors.ts#TeamVerifyError',
      'apps/runner/src/modes/worker/team/session/self-review.ts#SelfReviewRestoreError',
      // The merge gate's own workflow-widening verdict, run on the box before the commit and
      // unmoved by a correction round (`gate_path_widens`, 2026-11-01): a gate refusing the diff.
      'apps/runner/src/modes/worker/team/session/gate-path-widening.ts#GatePathWideningError',
      // The repo's own commit hook (`commit_hook_refused`), the repo's own gate before the
      // land (`verify_red`) and the repo's own pre-push hook graded and refused
      // (`push_hook_red`, 2026-11-01) — each the repository's gate refusing the round's diff.
      'apps/runner/src/modes/worker/followup-land-error.ts#FOLLOWUP_LAND_WIRE_CODE',
      // The conflict lane's push, refused by the same pre-push hook (`push_hook_red`).
      'apps/runner/src/modes/worker/conflict-merge-error.ts#PUSH_HOOK_RED',
    ),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: true,
    failureRateExcluded: false,
    copy: {
      alert:
        "A gate refused the change this run produced, so nothing was committed: the repository's own verify command exited non-zero, or the platform's merge gate refused a workflow edit a platform pull request may not make. That is the gate working — the answer is in the diff.",
      unclaimed:
        'No box claimed this — the gate that refused the last attempt would refuse a fresh checkout of the same change.',
      ran: "A gate refused the change this run produced, so nothing was committed: the repository's own verify command exited non-zero, or the platform's merge gate refused a workflow edit a platform pull request may not make. That is the gate working — the answer is in the diff.",
    },
  },
  /**
   * THE SCOUT REFUSED TO GROUND HERE because the checkout is on disk but is NOT
   * the proven default branch — the survey would read a `dz/task-…` worktree, not
   * the trunk, and a verdict built on it cannot be trusted. This is the
   * `tree_unproven` arm of `scoutGroundingFault`
   * (`apps/runner/src/modes/scout/executor.ts`), split out of the old
   * `survey_brief` arm alongside the `survey_unwired` half (which folds into
   * {@link ToolUnavailable}).
   *
   * MEASURED, 2026-09-04: 69 of 685 `scout_survey` rows carry the
   * `tree_gap: 'wrong_ref' | 'not_proven'` payload — the population today's
   * `ScoutTreeGap` audit event (`packages/domain/src/value-objects/hive-event.ts`)
   * names for the clone-vs-default-branch fault. Today none of them reach a
   * `failure_kind` column — `scout_tree_gap` is a {@link HiveEvent}, not a
   * `TaskFailureKind` — and that is the gap this entry closes:
   * `scout_tree_unproven` is what the wire `code` map
   * `SCOUT_GROUNDING_WIRE_CODE` (`apps/runner/src/modes/scout/executor.ts`)
   * stamps from the `ScoutGroundingMissingError` site, so the population ends
   * up counted under its cause rather than under the box.
   *
   * Three siblings, each distinct from this one in a named way — the borders
   * are the whole point of a fourth scout-shaped member:
   *   {@link ScoutSurveyUnwired} — the SURVEY SEAM is ABSENT on this box (no
   *                              nameable model reached the scout lane, or
   *                              `DZ_SCOUT_MAX_ROUNDS=0`): the `survey_unwired`
   *                              arm of the old `survey_brief` split, lifted
   *                              out of `tool_unavailable` by #3402 because
   *                              its ladder was wrong (a transient box-local
   *                              fault under a single-attempt deterministic
   *                              budget — executor.ts`'s
   *                              `SCOUT_GROUNDING_WIRE_CODE` now wires it to
   *                              `'scout_survey_unwired'`). Same charge posture
   *                              — `Charged`, finite ladder, exhausted
   *                              `Fail` —
   *                              but the UNWIRED case: "no fix was produced",
   *                              and the operator move is provision a model
   *                              or re-enable the survey.
   *   {@link GateUnrunnable}   — the GATE's OWN refusal (a 126/127 exit, or a
   *                              75 `EX_TEMPFAIL`); the survey's tree check
   *                              runs green on this box but rejects the cloned
   *                              HEAD — this kind is what is raised AFTER the
   *                              gate ran and only when the HEAD is wrong.
   *                              Same posture for the same reason: a box-state
   *                              fault that a fresh box may clear.
   *   {@link BoxBaselineRed}   — a `node_modules` fault on the box: the
   *                              INSTALLED tree differs from what the lockfile
   *                              says. Distinct because the INSTALLED state is
   *                              broken, not the CHECKED-OUT state — that
   *                              hole is bigger than a git refresh clears.
   *
   * Budgeted identically to {@link GateUnrunnable}: `Charged`, the standard
   * {@link TASK_MAX_ATTEMPTS} ladder, exhausted `Fail`. A redelivery routes
   * through `failOrRetryTask` → `claimNextReady`, which clears `runner_id` and
   * carries no previous-runner exclusion — the same box may reclaim it, and a
   * different one routinely will. The clone-vs-default-branch fault is
   * BOX-LOCAL, so a fresh box may have the proven default branch checked out
   * and the ladder buys a real chance of recovery; `Uncharged` would re-queue
   * on a customer's BYOK credential that DID spend attempts, and that is the
   * #2567 loop reached through the checkout door.
   *
   * Produced by `apps/runner/src/modes/scout/executor.ts#ScoutGroundingMissingError`
   * at the `tree_unproven` arm of `scoutGroundingFault`; the wire `code` map
   * `SCOUT_GROUNDING_WIRE_CODE` there stamps this kind under the
   * `'scout_tree_unproven'` code.
   *
   * Producer:
   * The `tree_unproven` arm of `scoutGroundingFault`: ONE producer (the
   * `ScoutGroundingMissingError` constructor throws verbatim from
   * `scout/executor.ts`'s precondition check), and the wire `code` map
   * `SCOUT_GROUNDING_WIRE_CODE` there names this kind under
   * `'scout_tree_unproven'`. The audit `HiveEvent.ScoutTreeGap` is emitted
   * separately by the same arm — the failure kind and the audit event are
   * produced by the same throw site but go to different seams.
   *
   * Budget:
   * Same posture as `gate_unrunnable`: `Charged`, the FULL `TASK_MAX_ATTEMPTS`
   * ladder, exhausted `Fail` (this kind names the box, never the work). The
   * clone-vs-default-branch fault is box-LOCAL
   * — a different box may have the proven default branch checked out — so a
   * redelivery has a real chance wherever `claimNextReady` puts it, exactly
   * as #3164 argued for the gate arm: this kind and `gate_unrunnable` share
   * the heterogeneous-fleet reason, not the budget by coincidence. `Uncharged`
   * would re-queue on a BYOK credential that DID spend attempts, and that is
   * the #2567 loop reached through the checkout door — this row's docblock
   * draws the sibling borders with `tool_unavailable` /
   * `gate_unrunnable` / `box_baseline_red`.
   *
   * Babysit round:
   * The scout's checkout was not the proven trunk — box state; not a babysit producer.
   *
   * Box-at-fault:
   * A scout precondition that no box met on this run (#3281). The checkout
   * was not the proven default branch, so the run never started. A fresh
   * box may have its own clone where the tree IS the default branch, so
   * this is NOT a box fault — it is a per-clone precondition gap. Same
   * posture as `GateUnrunnable` (`./box.ts`; transient across replicas) and
   * `PlanDependencyFailed` (no machine to inspect).
   *
   * Alert sentence:
   * Split from the prior `survey_brief` arm (#3281) so the wire code names the cause (tree,
   * not survey). A precondition refused the run because the checkout was NOT the proven
   * default branch — the run timeline's `scout_tree_gap` event carries the structural detail.
   *
   * Reason sentence:
   * Split from the previous `survey_brief` arm (#3281) so the wire code names
   * the cause. The precondition refused the run before any box started because
   * the checkout was NOT the proven default branch — the run timeline's
   * `scout_tree_gap` event carries the structural detail.
   */
  ScoutTreeUnproven: {
    value: 'scout_tree_unproven',
    ordinal: 30,
    producer: wire('apps/runner/src/modes/scout/executor.ts#ScoutGroundingMissingError'),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The scout precondition found the checkout was not the proven default branch, so the run never started. Refresh the checkout against the default branch; the run timeline’s `scout_tree_gap` event names which branch the clone landed on.',
      unclaimed:
        'No box claimed this — the scout precondition refused the run because the checkout was not the proven default branch; the run timeline’s `scout_tree_gap` event names which branch the clone landed on.',
      ran: 'The scout precondition found the checkout was not the proven default branch, so the run never started. The run timeline’s `scout_tree_gap` event names which branch the clone landed on.',
    },
  },
  /**
   * A REPO-ONBOARDING PASS THAT COULD NOT FINISH — the setup lane's `blocked`
   * verdict, settled as the failure it is instead of the success it looked like.
   *
   * MEASURED, 2026-09-08 (#3489): every `setup`-origin task that has EVER run in
   * production — six of them, 2026-08-27 through 2026-09-08 — is stored
   * `status = 'completed'`, and every one carries
   * `{"event":"onboarding_verdict","verdict":"blocked","prerequisite":"no_checkout"}`
   * on its run events. No run emitted `onboarding_land` or `pr_open`, no
   * `dz/onboarding` pull request exists on any of the five repos, and no
   * `repositories.ai_first_at` has ever been stamped by a pass. The mechanism was
   * one clean return: `settleVerdict` (`apps/runner/src/modes/setup/executor/
   * settle.ts`) returned normally for all three verdicts, so the run emitted
   * `run_finished` and `taskTransitionFor` (`apps/worker/src/fleet/
   * event-sink.ts`) mapped it to {@link TaskStatus.Completed} — whose own
   * docblock reads *"Work landed as a PR; the babysit watch takes over from
   * here."* Nothing landed, and the babysit watch was handed a task with no pull
   * request to watch. That is what kept a dead lane invisible for thirteen days:
   * a success-shaped failure counts as a pass in every operator read that groups
   * by status.
   *
   * THE POPULATION IS A CLOSED VOCABULARY, not a free-text reason, and since #4362 it
   * is IN THIS PACKAGE: {@link SETUP_BLOCKERS}
   * (`../../value-objects/task-run-event/onboarding-blocker.ts`), beside the
   * `OnboardingVerdictPayloadSchema` field a box mints it for, each member carrying its
   * own docblock. It used to live in `apps/runner/src/modes/setup/verdict.ts`, where no
   * consumer outside the box could import it — so this docblock RESTATED the list in
   * prose and a test in that app read the old table file's text to hold the two together. The
   * prose went stale twice anyway (it lacked `no_model_credential` and
   * `unsupported_stack` until arc 101 slice 03 added them), which is what a guard
   * reading English rather than a symbol buys. There is one list now, so there is
   * nothing left to hold it to.
   *
   * WHICH SIDE OF THE TERMINAL/RETRYABLE LINE a member falls on is
   * {@link ONBOARDING_BLOCKER_CLEARED_BY}, the one table the box's wire `code` and the
   * mothership's maintainer notification both derive from — total over `SetupBlocker`,
   * so a new blocker does not compile until it is answered for.
   *
   * WHICH one it was rides the error message and the `onboarding_verdict` event,
   * so an operator gets the remedy without opening run events; the COLUMN answers
   * the coarser question this vocabulary is for — why is this row dead.
   *
   * TERMINAL, NOT CHARGED, and the reason is structural rather than a judgement
   * about flakiness: every member THIS kind still carries is a PREREQUISITE a human
   * or an operator satisfies — mint the token, name the default branch, grant the
   * App permission, name a supported stack, put bun on the box — and none of them is
   * cleared by re-running the pass, on this box or a fresh one.
   * {@link DependencyFailed} is the precedent and the shape is identical: terminal
   * on purpose, never retried, no fault of the task. The one objection #3489 raises
   * against `failed` — that a missing prerequisite must not burn retry budget — is
   * answered HERE, by {@link TASK_FAILURE_BUDGET}, not by choosing a softer status.
   *
   * "CLEAN THE TREE" USED TO BE ON THAT LIST AND IS NOT (#4362). The two tree blockers
   * — `dirty_worktree` and `untracked_collision` — name PER-BOX state, not an account or
   * repository fact, and THE PASS ITSELF REPAIRS IT: step 0'' of
   * `apps/runner/src/modes/setup/executor/run.ts` runs the coding lane's
   * `preflightTaskTree` at task start, parking the dirt and landing the tree on the
   * fetched base before the redelivery writes a byte. So the redelivery heals with
   * nothing human in the loop — and, unlike the argument this paragraph used to make, it
   * heals whether or not any other lane ever touches that box — it never rests on the
   * box's NEXT CODING TASK doing the parking (when setup ran on boxes PINNED `setup`, no
   * coding task was ever offered to one). They are graded
   * {@link OnboardingTreeDirty} instead, per {@link ONBOARDING_BLOCKER_CLEARED_BY}. A
   * dirty box tree filed here was the premise that made two production setup passes
   * terminal at zero attempts on 2026-09-17.
   *
   * AND NOT A NEW `TaskStatus`, which is the decision this kind exists to avoid
   * re-opening. `completed` is a lie (its docblock promises a landed PR),
   * `cancelled` reads as an operator act and nobody cancelled, and `failed` is
   * what puts the row in `TaskLifecycleBucket.Failed` so operator reads stop
   * counting a blocked pass as a pass. The status enum is a one-way door on a
   * stored shape; `tasks.failure_kind` is `text`
   * (`packages/db/src/schema/tasks.ts`), so a new KIND needs no migration and is
   * exactly the column built to carry *why*.
   *
   * Produced by the wire `code` map ({@link TASK_FAILURE_WIRE_CODE}) — the box
   * stamps it at `OnboardingBlockedError`
   * (`apps/runner/src/modes/setup/executor/settle.ts`), raised AFTER the
   * `onboarding_verdict` event is published and after the pass has reported its
   * summary on the mailbox, never instead of either. That event is the wire
   * contract the mothership stamps `ai_first_at` from on a `ready` verdict, and a
   * `blocked` pass still owes the audit chain its reason — the failure is how the
   * TASK settles, not a licence to go quiet.
   *
   * Producer:
   * #3489: ONE producer — the `OnboardingBlockedError` constructor, which stamps the
   * code and is thrown by `runSetup` AFTER the `onboarding_verdict` event and the
   * mailbox summary have both gone out. The throw is deliberately the pass's LAST
   * act rather than `settleVerdict`'s: raising it at the verdict would skip the
   * summary a human reads and make the "dispatch nothing on `blocked`" branch
   * (`executor/run.ts` step 6a) unreachable, and an unrun branch is the thing this
   * repo refuses. `loop.ts` forwards `err.code` onto the run's terminal `error`
   * event unchanged, exactly as it does for the scout and coding lanes.
   *
   * THE ANCHOR IS THE MINT, NOT THE THROW SITE, and only since #4362. This list's
   * contract is "every site that STAMPS the code", and `task.test.ts` checks it by
   * reading the named file for the literal — so it has to name the file the literal is
   * in. `OnboardingBlockedError` no longer holds one: its `code` is
   * `onboardingBlockerFailureKind(verdict.prerequisite)`, which reads
   * `ONBOARDING_BLOCKER_CLEARED_BY` — the one table the mothership's maintainer
   * notification derives from too, so the budget and the sentence a human reads cannot
   * disagree about a blocker. The throw site is unchanged and is still
   * `apps/runner/src/modes/setup/executor/settle.ts#OnboardingBlockedError`; it is named
   * here in prose because naming it in `producedBy` would assert a stamp that is not
   * there. The mint RUNS on every blocked pass — it has exactly one call site, that
   * constructor — which is this Producer section's caveat ("the anchor must name a seam that runs")
   * answered rather than assumed.
   *
   * Budget:
   * TERMINAL (#3489), for {@link DependencyFailed}'s reason rather than a judgement
   * about flakiness: every prerequisite this kind STILL carries is satisfied by a
   * HUMAN or an OPERATOR — mint the git token, name the default branch, grant the
   * App's `workflows` permission, name a supported stack, put bun on the box — and
   * none of them is cleared by re-running the pass on a fresh box. This is also the
   * whole answer to the objection #3489 raises against settling the pass `failed` at
   * all: a missing prerequisite must not burn retry budget, and the BUDGET is where
   * that is said, not the status.
   *
   * "CLEAN THE TREE" WAS ON THAT LIST UNTIL #4362 AND WAS NEVER TRUE OF IT. A dirty
   * working tree is one box's disk, not a fact about the account or the repository: the
   * same task on any other box never meets it, and on THIS box the setup pass itself
   * repairs it — step 0'' of `apps/runner/src/modes/setup/executor/run.ts` runs the
   * coding lane's `preflightTaskTree` at task start, so the redelivery lands on a tree
   * the pass already parked and re-based. It is the PASS that heals it, not some other
   * lane passing through. The two tree blockers moved to
   * {@link TaskFailureKind.OnboardingTreeDirty} (`./box.ts`); nothing else did, and which
   * blocker is which is `ONBOARDING_BLOCKER_CLEARED_BY`
   * (`../../value-objects/task-run-event/onboarding-blocker.ts`), not a second list.
   *
   * NOT `Uncharged`, which is the other zero-attempt arm and would be wrong in the
   * dangerous direction FOR WHAT IS LEFT HERE: uncharged means re-enter the queue
   * forever, so a repo whose App is missing a permission would re-dispatch a setup
   * pass against the same refusal until a human noticed — the #2567 loop reached
   * through the onboarding door, and with no pacer on this lane at all.
   *
   * Babysit round:
   * The setup lane's own verdict on the repo; waiting does not clear it.
   *
   * Box-at-fault:
   * #3489: a repo-onboarding pass stopped on a prerequisite — a git token nobody
   * could mint, an unnamed default branch, a missing App permission, a tree
   * carrying other work. Every one of those is a fact about the ACCOUNT or the
   * REPO, identical on every box in the fleet, so paging a machine sends an
   * operator to inspect hardware that is working perfectly. Same posture as
   * `CheckoutUnavailable` (`./box.ts`), whose causes are the same ones off the box.
   *
   * Alert sentence:
   * #3489. Until this kind existed a blocked onboarding pass was stored `completed`.
   *
   * Reason sentence:
   * #3489. The PROPERTY every `SetupBlocker` graded HERE shares is that it is a fact
   * about the account or the repository — a credential, a branch, an App permission —
   * which no box can work around and no retry can change. A dirty tree was on that
   * list until #4362 and never belonged: it is one box's disk, the pass repairs it on
   * its own next run, and it grades `onboarding_tree_dirty` (`OnboardingTreeDirty`, `./box.ts`).
   * `ONBOARDING_BLOCKER_CLEARED_BY` (`@developerz/domain`) is which is which.
   * The sentence states that
   * and points at the run's `onboarding_verdict` for WHICH one, rather than listing
   * the members: the list here named four of the five (`no_checkout` was missing) and
   * would have gone stale again on the next member added. It also says the row will
   * not be retried, because the budget is `Terminal` and a reader who waits for a
   * retry that never comes is the failure this sentence exists to prevent.
   */
  OnboardingBlocked: {
    value: 'onboarding_blocked',
    ordinal: 32,
    producer: wire(
      'packages/domain/src/value-objects/task-run-event/onboarding-blocker.ts#onboardingBlockerFailureKind',
    ),
    budget: terminal(),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'Repo onboarding stopped on a prerequisite only a human can satisfy — a fact about the repository or the account that no box can work around. The run’s `onboarding_verdict` event names which; nothing was opened and the repo stays un-onboarded.',
      unclaimed:
        'No box claimed this — repo onboarding was already blocked on a prerequisite the platform cannot satisfy itself, so the row was turned away before dispatch.',
      ran: 'Repo onboarding could not finish: it hit a prerequisite only you can satisfy — something about the repository or the account that no box can work around. Nothing was opened and the repo stays un-onboarded; the run’s `onboarding_verdict` names which one. This is not retried — satisfy it and run setup again.',
    },
  },
  /**
   * THE MODEL'S CONFLICT RESOLUTION WAS REFUSED (#2235). The harness merged the base into
   * the PR branch, the agent edited the conflicted files, and the harness's own check of
   * that edit refused to commit it: a path outside the conflict set and the PR's own diff
   * was modified (#280), a conflicted file still carries markers, or a path is still
   * unmerged after staging. The
   * guard is right every time; what it names is the resolution the model produced.
   *
   * WHY IT IS ITS OWN KIND: these refusals carried a typed `ConflictMergeRefusal` reason and
   * no `code`, so every one graded as {@link RunnerError}, the uncoded residual. Measured in
   * prod on 2026-09-17 (last 48 h, settled `pr_babysit` tasks): 11 of 16 failures were
   * `runner_error`, and 9 of those 11 were the out-of-conflict-set refusal alone. A reader of
   * a babysit failure saw a box fault where the box had done its job.
   *
   * ONLY THE MODEL-SHAPED THROWS STAMP IT. The same `stray_changes` and `unresolved_paths`
   * reasons are also thrown when git could not ANSWER the question (`git diff` exited
   * non-zero); those are box or git faults, stay uncoded, and grade as {@link RunnerError}.
   *
   * Budgeted identically to {@link RunnerError}: the split changes the LABEL, never the
   * spend. Whether a refused resolution should spend attempts at all is a separate decision,
   * and this kind is what makes it measurable.
   *
   * Produced at `apps/runner/src/modes/worker/conflict-merge-error.ts#ConflictMergeError`.
   *
   * Producer:
   * #2235 — the harness refused the model's conflict resolution (out-of-set edit, markers
   * left, still unmerged). Stamped per throw site, never per reason: the same reasons are
   * thrown when git could not answer, and those stay uncoded.
   *
   * Budget:
   * #2235: split out of `runner_error`, which is where every refused conflict resolution
   * graded. The split changes the LABEL, never the spend — the same row as `runner_error`.
   *
   * Babysit round:
   * The harness refused the model's conflict resolution (#2235) — charged, as it was under `runner_error`.
   *
   * Box-at-fault:
   * #2235: the harness refused the MODEL's conflict resolution — the box's guard worked.
   * Graded `runner_error` before, which is `true` here, so each one paged a healthy box.
   *
   * Alert sentence:
   * THE BOX DID ITS JOB AND WHAT FAILED IS THE BRANCH OR THE MODEL'S EDIT — graded as a box
   * fault before each was named, so each sentence says so.
   *
   * - #2235 `conflict_resolution_refused`: split out of `RunnerError`, where every refused
   *   resolution read as a box fault.
   * - #5077 `tree_preconditions_unmet`: setup PASSED on the box and the gate still refused —
   *   graded `gate_unrunnable` before, which blamed the one box just cleared.
   *
   * Reason sentence:
   * This row's copy of the MODEL-OUTCOME sentence family (read by `failureReasonSentence`, `apps/api/src/routes/tasks.ts`): the box reached the
   * model and did its job, and what failed is what the model produced.
   * `model_produced_no_work` moved here verbatim when #2235 added the second kind.
   *
   * #2235 — split out of `RunnerError`, where every refused resolution read as a box
   * fault. Charged, so attempt 0 with this kind is a REDISPATCH: the operator's retry
   * resets `attempt` and keeps `failure_kind`, so the refusal is an earlier run's.
   */
  ConflictResolutionRefused: {
    value: 'conflict_resolution_refused',
    ordinal: 45,
    producer: wire('apps/runner/src/modes/worker/conflict-merge-error.ts#ConflictMergeError'),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        "The harness refused to commit the model's conflict resolution: it edited a file outside the conflicting files and the PR's own changes, left conflict markers in a file, or left a path unmerged. The refusal is the harness working as designed, not a fault in the box; the run timeline's `babysit_conflict_refused` event names which check refused, and on which paths.",
      unclaimed:
        'An earlier run’s conflict resolution was refused by the harness, and no run has picked the task up since it was queued again. The run timeline’s `babysit_conflict_refused` event on that earlier run names which check refused, and on which paths.',
      ran: 'The harness refused to commit the model’s conflict resolution: it edited a file outside the conflicting files and the PR’s own changes, left conflict markers in a file, or left a path unmerged. The refusal is the harness working as designed, not a fault in the box; the run timeline’s `babysit_conflict_refused` event names which check refused, and on which paths.',
    },
  },
  /**
   * THE GATE STILL REFUSES THIS TREE AFTER SETUP PASSED ON THIS BOX (#5077). The repo's own
   * gate refused to grade with exit 75 (`preconditions_unmet`), the lane re-ran the repo's
   * `bin/setup` in the run's tree (`harness/check-repair.ts`) and it PASSED, and the gate
   * refused again, until the harness's latch ended the run. The box has just shown it can
   * set this repo up; what setup cannot repair is in the TREE — the branch, or the edit the
   * run made to it.
   *
   * MEASURED (tesote/tesote.ai#2603, 2026-09-25, runner `0.0.25+sha-0c74a72`): every one of
   * the 5 runs whose post-edit repair passed ended in the same refusal, graded
   * {@link GateUnrunnable}, "an environment fault of this box". The cause was the branch:
   * `bin/dev doctor --strict` failing on stale compose wiring a `resolve_conflict` round
   * could not edit. The box was excluded for 30 minutes per run, each task spent its full
   * ladder on identical refusals, and the babysit round was uncharged, so it re-minted.
   *
   * WHY NOT {@link GateUnrunnable}: that kind is box-local and excuses the babysit round, both
   * of which rest on the box being the cause, and here the box is the one party that has
   * just been cleared. NOR {@link VerifyFailed}: the gate never graded the change.
   *
   * CHARGED, ONE attempt, `Fail`: a second box sets up the same tree and meets the same
   * refusal (5 of 5 in the MEASURED paragraph), so a retry buys a re-run of the same evidence. Not box-local.
   * The babysit round is charged ({@link BABYSIT_ROUND_CHARGE}), so a branch that keeps
   * refusing reaches its ladder's end and a human, instead of re-minting.
   *
   * A SECOND ARM, SAME FACT (tesote/tesote.ai#2691, 2026-09-27): setup FAILED, on a PR tree
   * whose own diff changed what setup reads (a compose file, a lockfile, `bin/setup`, a path
   * setup's output names — `apps/runner/src/modes/worker/setup-inputs.ts`). The lane hands the
   * model that failure to fix instead of ending the run; if the gate's setup repair still
   * fails on those files, the latch ends here too (`setup_broken_by_change` on the verdict).
   * 16 runs over 6 `pr_babysit` tasks had been graded {@link GateUnrunnable} on a compose
   * rename the branch made. The tree is the cause and the box is not, so the budget holds.
   *
   * A THIRD ARM, SAME FACT: setup fails on a PR head BEHIND its base and PASSES on the same
   * box once that base is merged in (`apps/runner/src/modes/worker/tree-setup.ts`
   * `RunTreeStaleBaseError`; the merge is a probe, aborted, never committed). The branch is
   * stale; the box just set the merged tree up. Graded {@link GateUnrunnable} with the off-base
   * mark before: kept out of the repo fault, but box-local and uncharged, so the round
   * re-minted on the same head (tesote/tesote.ai since 2026-09-26: 23 runs over 8 tasks on
   * #2683, 11/5 on #2669, 11/4 on #2695). The passing probe row is the evidence the babysit
   * driver reads to merge the base in on its next pass, past its usual refresh gates, once per
   * base sha (`apps/worker/src/babysit/base-refresh.ts`, #5412).
   *
   * THE RESIDUAL, named: a `bin/setup` that exits 0 without repairing what the gate probes is
   * a box fault this kind would charge. The repo's setup is the repo's contract for "this
   * checkout is set up"; a setup that lies is graded against that contract.
   *
   * Produced at `apps/runner/src/harness/check-refusal.ts#reportEditVerdict`.
   *
   * Producer:
   * #5077 — the single-adapter lane's latch, when the refusal persisted after a green
   * precondition repair (`harness/check-repair.ts` tags the outcome, the verdict carries it).
   *
   * Budget:
   * #5077: CHARGED, ONE attempt, `Fail` — setup passed on this box and the gate still
   * refused, so the tree is the cause and another box sets up the same tree. NOT box-local.
   *
   * Babysit round:
   * The gate refused this tree again after `bin/setup` PASSED on the box (#5077) — the branch
   * is the cause, and an uncharged round re-minted on it every sweep (tesote/tesote.ai#2603).
   *
   * Box-at-fault:
   * #5077: the gate still refused after setup PASSED on this box — the tree is the cause, and
   * this box is the one party just cleared. Graded `gate_unrunnable` before, which pages it.
   *
   * Alert sentence:
   * THE BOX DID ITS JOB AND WHAT FAILED IS THE BRANCH OR THE MODEL'S EDIT — graded as a box
   * fault before each was named, so each sentence says so.
   *
   * - #2235 `conflict_resolution_refused`: split out of `RunnerError`, where every refused
   *   resolution read as a box fault.
   * - #5077 `tree_preconditions_unmet`: setup PASSED on the box and the gate still refused —
   *   graded `gate_unrunnable` before, which blamed the one box just cleared.
   *
   * Reason sentence:
   * This row's copy of the GATE-REFUSAL sentence family (read by `failureReasonSentence`, `apps/api/src/routes/tasks.ts`): the repository's own
   * verify command reached no verdict. `gate_unrunnable` moved here verbatim when
   * #5077 added the second kind, which differs on the one fact a reader acts on — whose fault.
   * Budget: charged once, not box-local (attempt 0 is a redispatch).
   *
   * The mothership writes this kind too, regrading a box's `gate_unrunnable` setup refusal
   * whose `setup_cause.class` is `tree` (`setupCausePolicy` -> `repoEnvFailureKind`,
   * `apps/worker/src/fleet/`). That lane is deliberately NOT a `producedBy` entry: a `wire`
   * entry is a throw site that STAMPS the code and is checked against its source
   * (`task.test.ts`), and neither function stamps one — they return a kind the event sink
   * writes to the column. A row declares ONE seam, and this one's is `wire` because a box
   * does raise it; `repo_env_down` is the same regrade's `platform` half, where the
   * mothership is the only writer.
   */
  TreePreconditionsUnmet: {
    value: 'tree_preconditions_unmet',
    ordinal: 46,
    producer: wire(
      'apps/runner/src/harness/check-refusal.ts#reportEditVerdict',
      'apps/runner/src/modes/worker/tree-setup.ts#RunTreeStaleBaseError',
    ),
    budget: charged(1, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        "The repository's setup or verify command cannot work on this branch as it stands, and the box is not the cause: setup passed on this box and the verify command still refused (exit 75), setup failed on files this pull request changes, or setup passes here once the branch takes its base. The run timeline's `native_harness_check` and `run_tree_setup` rows carry what each printed.",
      unclaimed:
        'An earlier run found this branch cannot be set up or graded as it stands, and the box was not the cause — setup passed there while the verify command refused, failed on files the pull request changes, or passed once the base was merged in — and no run has picked the task up since it was queued again. The run timeline’s `native_harness_check` and `run_tree_setup` rows carry what each printed.',
      ran: 'The repository’s setup or verify command cannot work on this branch as it stands, and the box is not the cause: setup passed on this box and the verify command still refused (exit 75), setup failed on files this pull request changes, or setup passes here once the branch takes its base. The cause is in the branch — its files, this run’s edit to them, or a base it has not merged. The change was never judged. The run timeline’s `native_harness_check` and `run_tree_setup` rows carry what each printed; fix what they name on the branch (or merge its base), then retry.',
    },
  },
  /**
   * A person is pushing to the pull request this `pr_babysit` round works on, so the box did
   * not start the round — or stopped it on a head the person just moved — and the task waits
   * for the branch to go quiet (`../../babysit/human-quiet`, 2026-09-28). Not a failure of
   * anything: the work was never attempted on a head that was about to move.
   *
   * Producer:
   * The babysit lane's quiet gate, before the round starts and when the head watch sees a
   * person's push mid-run (`HumanPushingError`).
   *
   * Budget:
   * Uncharged and uncapped: the wait is on a person, and a count of their pushes is not a
   * reason to end the task. What bounds it is the surface, not a cap: a PR deferred past
   * `HUMAN_PUSHING_SURFACE_MS` is named on `next_steps`.
   *
   * Babysit round:
   * A person moved the head — a race with a human, not the fix.
   *
   * Box-at-fault:
   * Repo state and dispatch-time facts, through `NoPrOpened`. The same box runs the next
   * task fine, and the next box would defer this one the same way.
   *
   * Reason sentence:
   * 2026-09-28 — a person is pushing to the pull request, so the babysit round waits for the
   * branch to go quiet; nothing was attempted and nothing was charged.
   */
  HumanPushing: {
    value: 'human_pushing',
    ordinal: 48,
    producer: wire('apps/runner/src/modes/worker/babysit/human-quiet.ts#HumanPushingError'),
    budget: uncharged(),
    park: {
      reason: TaskHoldReason.PrQuietWindow,
      backstopMs: HUMAN_PUSH_QUIET_MS,
      honorsStatedReset: true,
    },
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'A person is pushing to this pull request, so the babysit round is waiting for the branch to go quiet — nothing failed.',
      unclaimed:
        'Waiting for a person to stop pushing to this pull request: the babysit round starts on its own once nobody has pushed for the quiet window.',
      ran: 'Not charged: a person is pushing to this pull request, so the babysit round waits for the branch to go quiet and starts on its own after that.',
    },
  },
  /**
   * The review box read the head's CI still PENDING at claim (#6718), so it did not start the
   * review and the task waits for CI to settle. Named apart from the hold it parks on
   * (`ci_pending`): `failure_kind` and `hold_reason` answer two questions in disjoint tokens. Not a failure of anything: the work was never
   * attempted on a head whose CI may still refuse it.
   *
   * Producer:
   * The review lane's relevance check at claim (`ReviewCiPendingError`), whose message is
   * `reviewCiPendingMessage` (`../review-relevance`), carrying the head's CI-ceiling instant
   * (`reviewCiHoldUntil`).
   *
   * Budget:
   * Uncharged and uncapped: the wait is on CI, and a count of re-checks is not a reason to end
   * the review. What bounds it is the box's CI ceiling — past it the box reviews anyway, and
   * discloses that CI had not settled.
   *
   * Park:
   * `ci_pending` until the instant the box stated (`honorsStatedReset`) — the head's CI
   * ceiling — else `CI_WAIT_CEILING_MS` from now. A CI completion on the head (or the
   * worker's sweep) releases it sooner; the clock is never the wake.
   *
   * Box-at-fault:
   * No — the head's CI state, which every box would read the same.
   *
   * Reason sentence:
   * 2026-10-20 — reviews wait for green CI, like a human (owner ruling on #6718).
   */
  ReviewCiPending: {
    value: 'review_ci_pending',
    ordinal: 61,
    producer: wire('apps/runner/src/modes/worker/review/relevance.ts#ReviewCiPendingError'),
    budget: uncharged(),
    park: {
      reason: TaskHoldReason.CiPending,
      backstopMs: CI_WAIT_CEILING_MS,
      honorsStatedReset: true,
    },
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: true,
    copy: {
      alert:
        "This pull request's CI has not finished on its latest commit, so the review is waiting for it — nothing failed.",
      unclaimed:
        "Waiting for this pull request's CI to finish on its latest commit: the review starts on its own once CI settles.",
      ran: "Not charged: this pull request's CI had not finished on its latest commit, so the review waits and starts on its own once CI settles.",
    },
  },
  /**
   * The review box found the claimed review NO LONGER RELEVANT at claim (#6718): the head's
   * CI went red, the PR is closed or merged, its head moved on, or it is a draft. The reason
   * rides the message (`reviewIrrelevantMessage`, `../review-relevance`), and the mothership
   * lands the row `cancelled` with that reason (`cancelIrrelevantReview`, `packages/db`), so
   * the row reads as skipped work, not a failure.
   *
   * Producer:
   * The review lane's relevance check at claim (`ReviewIrrelevantError`).
   *
   * Budget:
   * Terminal, never charged. This budget is only what `failOrRetryTask` does if a report
   * reaches it un-intercepted (a message carrying no reason this build knows): the review
   * ends rather than re-running a check that already said no, and nothing is billed.
   *
   * Box-at-fault:
   * No — the PR's own state.
   *
   * Reason sentence:
   * 2026-10-20 — a review runs only when relevant, like a human's (owner ruling on #6718).
   */
  ReviewIrrelevant: {
    value: 'review_irrelevant',
    ordinal: 62,
    producer: wire('apps/runner/src/modes/worker/review/relevance.ts#ReviewIrrelevantError'),
    budget: terminal(),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: true,
    copy: {
      alert:
        'The review was skipped: by the time a server picked it up, the pull request no longer needed it (its CI failed, it was closed, merged or marked draft, or a newer commit replaced it).',
      unclaimed:
        'Skipped: the pull request no longer needed this review — its CI failed, it was closed, merged or marked draft, or a newer commit replaced it.',
      ran: 'Not charged: the review was skipped because the pull request no longer needed it — its CI failed, it was closed, merged or marked draft, or a newer commit replaced it.',
    },
  },
  /**
   * THE TASK'S OWN STORED ENVELOPE NO LONGER PARSES (2026-10-30). A row whose `tasks.envelope`
   * fails `TaskEnvelopeSchema` can never be handed to a box: the claim's own read
   * (`normalizeTask`) throws, the claim transaction rolls back, and the row is `pending` again,
   * unchanged, at the head of the queue — every tick, on every box of the account. Prod
   * 2026-10-30: two `scout` rows with a 13,661-char `problem` (cap `TASK_PROBLEM_MAX`, 10,000)
   * logged `claim_failed … too_big [envelope, problem]` across the fleet and mostly blocked
   * that account's coding claims. The write seam now parses before every insert
   * (`sealEnvelope`), so a NEW row cannot reach this; this kind ends the rows that already
   * sit in the table, and any a future schema tightening strands.
   *
   * Producer:
   * The mothership, after the claim that read the row rolled back: `failTaskEnvelopeInvalid`
   * re-reads the envelope under the row lock and fails it only if it still does not parse,
   * clamping over-cap strings (with a visible marker) so the row reads again; the original
   * envelope rides on the `task.failed` row written in the same transaction.
   *
   * Budget:
   * Terminal. A retry re-reads the same stored bytes; nothing on a box can change them. The
   * work is unblocked by re-filing it with a valid envelope.
   *
   * Babysit round:
   * Never claimed, never ran.
   *
   * Box-at-fault:
   * No — the row's own data. No box ever saw it.
   */
  EnvelopeInvalid: {
    value: 'envelope_invalid',
    ordinal: 66,
    producer: platform('packages/db/src/queries/tasks/envelope-invalid.ts#failTaskEnvelopeInvalid'),
    budget: terminal(),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'This task’s stored description no longer passes the task schema (a field over its size cap, or a shape this release refuses), so no box could ever be handed it. It was ended instead of blocking the queue. Re-file the work with a valid description.',
      unclaimed:
        'This task’s stored description failed the task schema, so no box could be handed it and the work never started. Re-file it with a valid description.',
      ran: 'This task’s stored description failed the task schema, so no box could be handed it again. Re-file the work with a valid description.',
    },
  },
  /**
   * THE CLAIM OF THIS ROW KEEPS ROLLING BACK THE SAME WAY (#7601). A claim reached the row and
   * its transaction rolled back on a deterministic throw that is not a hold and not an
   * unparseable envelope (those have their own endings), so the row went back to `pending`
   * unchanged, at the head of the claim order, and the next pass met the same throw — a
   * head row that blocks its account's queue. The mothership counts identical rollbacks per
   * (row, lease epoch, error signature) and ends the row once the same failure repeats
   * `CLAIM_ROLLBACK_LIMIT` times, spaced apart (`apps/worker`
   * `src/dispatch/claim-rollback-bound.ts`); the `task.failed` row carries the error.
   *
   * Producer:
   * The mothership, after the claim rolled back: `failTaskClaimRollbackExhausted`, CAS on
   * `pending` + the lease epoch the failing claims read + the account, with the caller's
   * `task.failed` row in the same transaction.
   *
   * Budget:
   * Terminal. The bound already WAS the retry budget: the same error, at the same epoch,
   * repeated across passes; another claim re-reads the same row and throws again.
   *
   * Babysit round:
   * Never claimed, never ran.
   *
   * Box-at-fault:
   * No — the claim is the mothership's; no box was handed the row.
   */
  ClaimRollbackExhausted: {
    value: 'claim_rollback_exhausted',
    ordinal: 67,
    producer: platform(
      'packages/db/src/queries/tasks/claim-rollback-exhausted.ts#failTaskClaimRollbackExhausted',
    ),
    budget: terminal(),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'Every attempt to hand this task to a server failed the same way on our side, several times over, so it was ended instead of blocking the queue. The error is on the task’s failure record; re-dispatch the task once the cause is fixed.',
      unclaimed:
        'Every attempt to hand this task to a server failed the same way on our side, so the work never started and the task was ended. Re-dispatch it once the cause is fixed.',
      ran: 'Every later attempt to hand this task to a server failed the same way on our side, so it was ended. Re-dispatch it once the cause is fixed.',
    },
  },
} as const satisfies Record<string, FailureKindRow>;
